import { useEffect } from 'react';
import { useRouter } from 'next/router';
import {
  AnalyticsEvent,
  initialize,
  setCheckoutDetails,
  track,
  setAdditionalContext,
} from '../../lib/analytics';
import { WindowWithDecibel } from '../../lib/analytics/decibel';
import { useAppConfig } from '../AppConfigContext';
import {
  useOrder,
  useSDKAnalytics,
  useExperienceKeys,
  useSDKOptions,
  usePlacementOptions,
  useSDKPromoOffers,
  useVirtualCard,
  useMerchantDetails,
  useProgramValues,
  useMerchantProgramValues,
  useMerchantPaymentProducts,
} from '../XPropsContext';
import { FCWithChildren } from '../../lib/types';
import { logger } from '../../lib/logger';

declare const window: WindowWithDecibel;

export const Analytics: FCWithChildren = () => {
  const config = useAppConfig();
  const { tenantID } = config;
  const { order, isDefaultOrder } = useOrder();
  const analytics = useSDKAnalytics();
  const { programID, merchantID, locationID } = useExperienceKeys();
  const { name: merchantName } = useMerchantDetails();
  const { name: programName } = useProgramValues();
  const { isDownPaymentEnabled } = useMerchantProgramValues();
  const promoOffers = useSDKPromoOffers();
  const { merchantPaymentProducts } = useMerchantPaymentProducts();
  const { route } = useRouter();
  const { isEmbedded, allowCheckout } = useSDKOptions();
  const isCarts = route.startsWith('/cart');
  const isInStore = route.startsWith('/in-store');
  const {
    isVirtualCard,
    isMerchantKeyEntryCapable,
    isMerchantTapToPayCapable,
  } = useVirtualCard();
  const { locationType } = usePlacementOptions();

  useEffect(() => {
    // Initialize analytics and track "modal_opened"
    initialize(config);
    track(AnalyticsEvent.ModalOpened);

    setCheckoutDetails({
      analytics,
      order,
      tenantID,
      programID,
      programName,
      merchantID,
      locationID,
      merchantName,
      promoOffers,
      merchantPaymentProducts,
      allowCheckout,
      isDefaultOrder,
      isEmbedded,
      isCarts,
      isInStore,
      isVirtualCard,
      isMerchantKeyEntryCapable,
      isMerchantTapToPayCapable,
      isDownPaymentEnabled,
      locationType,
    });
  }, []);

  useEffect(() => {
    const trackDecibelSessionID = () => {
      const decibelSessionID = window.decibelInsight?.<string>('getSessionId');

      if (!decibelSessionID) {
        logger.warn({ merchantID, programID }, 'Decibel session ID not found');
        return;
      }
      setAdditionalContext({
        decibel_session_id: decibelSessionID,
      });
    };

    // Check if decibelInsight is available and ready
    if (typeof window.decibelInsight !== 'undefined') {
      window.decibelInsight('ready', trackDecibelSessionID);
    } else {
      window._da_readyArray = window._da_readyArray || [];
      window._da_readyArray.push(trackDecibelSessionID);
    }
  }, []);

  return null;
};
